import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

export default function Reviews() {
    const [items, setItems] = useState(null);
    const [active, setActive] = useState(null);
    const [draft, setDraft] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/reviews");
        setItems(data);
        if (data.length && !active) setActive(data[0]);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    const suggest = async () => {
        if (!active) return;
        setBusy(true);
        try {
            const { data } = await api.post("/reviews/suggest-reply", { review_id: active.id });
            setDraft(data.draft);
            const combined = `${data.draft.greeting}\n\n${data.draft.body}\n\n${data.draft.apology}\n\n${data.draft.action}\n\n${data.draft.signoff}`;
            setReplyText(combined);
            toast.success("AI draft ready");
        } catch {
            toast.error("Could not draft reply");
        } finally {
            setBusy(false);
        }
    };

    const post = async () => {
        if (!active || !replyText.trim()) return;
        await api.post("/reviews/reply", { review_id: active.id, reply_text: replyText });
        toast.success("Reply posted");
        load();
    };

    return (
        <>
            <PageHeader
                title="Reviews & Reputation"
                subtitle="AI-classified sentiment with one-click drafted replies."
            />
            {!items ? (
                <LoadingBlock rows={6} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <Card className="lg:col-span-5">
                        <CardContent className="p-0">
                            <div className="max-h-[70vh] overflow-y-auto divide-y divide-border" data-testid="reviews-list">
                                {items.map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => {
                                            setActive(r);
                                            setDraft(null);
                                            setReplyText(r.reply_text || "");
                                        }}
                                        className={`w-full text-left p-3 hover:bg-secondary/50 ${active?.id === r.id ? "bg-secondary" : ""}`}
                                        data-testid="reviews-item"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-sm font-medium">{r.guest_name}</div>
                                            <StatusChip status={r.sentiment} />
                                        </div>
                                        <div className="text-xs text-muted-foreground mb-1">
                                            {r.source} · {r.rating}/5 · {r.language.toUpperCase()} {r.replied && "· replied"}
                                        </div>
                                        <div className="text-sm line-clamp-2">{r.text}</div>
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="lg:col-span-7">
                        <CardContent className="py-4">
                            {!active ? (
                                <div className="text-sm text-muted-foreground">Pick a review to draft a reply.</div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-medium">{active.guest_name} — {active.rating}/5</div>
                                        <StatusChip status={active.sentiment} />
                                    </div>
                                    <div className="text-sm bg-secondary/50 rounded-lg p-3 mb-4" data-testid="reviews-text">{active.text}</div>
                                    <div className="rounded-xl border border-border bg-[hsl(var(--accent))] p-4 space-y-2" data-testid="ai-reply-panel">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-medium flex items-center gap-1">
                                                <Sparkles className="w-4 h-4" /> AI-drafted reply
                                            </div>
                                            <Button size="sm" onClick={suggest} disabled={busy} data-testid="ai-reply-draft">
                                                {busy ? "Drafting…" : "Draft reply"}
                                            </Button>
                                        </div>
                                        <Textarea
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            rows={8}
                                            placeholder="Click 'Draft reply' to generate, then edit and send."
                                            data-testid="reviews-reply-text"
                                        />
                                        <div className="flex justify-end">
                                            <Button onClick={post} disabled={!replyText.trim()} data-testid="ai-reply-send">
                                                <Send className="w-4 h-4 mr-1" /> Send reply
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </>
    );
}
